import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Users, Eye, Timer, TrendingUp, TrendingDown, Radio, IndianRupee, Download, Copy, Percent, RefreshCw, Smartphone } from "lucide-react";

const PALETTE = ["#2E7D32", "#EA580C", "#D97706", "#2563EB", "#DB2777", "#059669", "#7C3AED", "#0891B2"];
const RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["7d", "7 days"], ["30d", "30 days"], ["month", "This month"]];

const maskMobile = (m) => (m && m.length >= 10 ? `${m.slice(0, 2)}XXXXXX${m.slice(-2)}` : m);

function Delta({ v }) {
  if (v === null || v === undefined) return null;
  const up = v >= 0;
  return <span className={`text-xs font-bold inline-flex items-center gap-0.5 ${up ? "text-thali-green" : "text-red-600"}`}>{up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{Math.abs(v)}%</span>;
}

function Card({ label, value, delta, icon: Icon, suffix }) {
  return (
    <div className="card-thali p-4" data-testid={`an-card-${label}`}>
      <div className="flex items-center justify-between">
        {Icon && <Icon className="w-5 h-5 text-thali-green" />}
        <Delta v={delta} />
      </div>
      <div className="text-xs text-thali-ink/60 mt-2">{label}</div>
      <div className="font-num font-extrabold text-2xl">{value}{suffix || ""}</div>
    </div>
  );
}

function ChartBox({ title, children }) {
  return (
    <div className="card-thali p-4">
      <div className="font-heading font-bold text-sm mb-3">{title}</div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

function Donut({ data }) {
  return (
    <PieChart>
      <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
        {data.map((d, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
      </Pie>
      <Tooltip /><Legend />
    </PieChart>
  );
}

function Funnel({ steps }) {
  const max = Math.max(1, ...steps.map((s) => s.value));
  return (
    <div className="space-y-2" data-testid="an-funnel">
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1].value : s.value;
        const conv = prev ? Math.round((s.value / prev) * 100) : 0;
        return (
          <div key={i}>
            <div className="flex justify-between text-xs mb-1"><span>{s.step}</span><span className="font-num font-bold">{s.value}{i > 0 && <span className="text-thali-ink/50"> · {conv}%</span>}</span></div>
            <div className="h-6 rounded-lg bg-thali-cream overflow-hidden"><div className="h-full rounded-lg" style={{ width: `${(s.value / max) * 100}%`, background: PALETTE[i % PALETTE.length], minWidth: s.value ? 8 : 0 }} /></div>
          </div>
        );
      })}
    </div>
  );
}

function fmtDur(sec) { const m = Math.floor(sec / 60); return m ? `${m}m ${sec % 60}s` : `${sec}s`; }

export default function AdminAnalytics() {
  const [tab, setTab] = useState("visitors");
  const [rng, setRng] = useState("7d");
  const [data, setData] = useState(null);
  const [live, setLive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/analytics/${tab}`, { params: { rng } }); setData(data); }
    catch { toast.error("Failed to load analytics"); } finally { setLoading(false); }
  }, [tab, rng]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const poll = () => api.get("/admin/analytics/live").then(({ data }) => setLive(data.live)).catch(() => {});
    poll(); const id = setInterval(poll, 30000); return () => clearInterval(id);
  }, []);

  const reveal = async (uid) => {
    try { const { data } = await api.post("/admin/analytics/reveal", { user_id: uid }); setRevealed((r) => ({ ...r, [uid]: data.mobile })); } catch {}
  };
  const waCopy = (row) => {
    const msg = `Namaste ${row.name} 🙏\nYour PM POSHAN MDM Calculator plan expires on ${(row.expiry || "").slice(0, 10)}. Renew for just ₹99/year to keep unlimited calculations & reports. 🔓\nvia pmposhanmdmcalculator.in`;
    navigator.clipboard.writeText(msg); toast.success("WhatsApp reminder copied");
  };
  const exportXlsx = (dash) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/export?dash=${dash}`;
    const tok = localStorage.getItem("pm_token");
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } }).then((r) => r.blob()).then((b) => {
      const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `analytics_${dash}.xlsx`; a.click();
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold">Analytics</h1>
        <span className="inline-flex items-center gap-1.5 bg-thali-green/10 text-thali-green rounded-full px-3 py-1 text-sm font-bold" data-testid="an-live"><Radio className="w-4 h-4 animate-pulse" /> {live} live now</span>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {[["visitors", "Visitors"], ["users", "Users"], ["subscribers", "Subscribers"]].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setData(null); }} data-testid={`an-tab-${k}`}
            className={`px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap ${tab === k ? "bg-thali-green text-white" : "bg-thali-paper border border-thali-turmeric/30 text-thali-ink/70"}`}>{l}</button>
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        {RANGES.map(([k, l]) => (
          <button key={k} onClick={() => setRng(k)} data-testid={`an-range-${k}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap ${rng === k ? "bg-thali-saffron text-white" : "bg-thali-cream text-thali-ink/70"}`}>{l}</button>
        ))}
        <button onClick={load} className="px-3 py-1.5 rounded-lg text-xs bg-thali-cream text-thali-ink/70" data-testid="an-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>

      {loading || !data ? <Loader /> : (
        <>
          {tab === "visitors" && <VisitorsTab d={data} />}
          {tab === "users" && <UsersTab d={data} />}
          {tab === "subscribers" && <SubscribersTab d={data} revealed={revealed} reveal={reveal} maskMobile={maskMobile} waCopy={waCopy} exportXlsx={exportXlsx} />}
        </>
      )}
      <StateRequestsCard rng={rng} />
    </div>
  );
}

function StateRequestsCard({ rng }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    const r = ["7d", "30d", "90d"].includes(rng) ? rng : "30d";
    api.get("/admin/analytics/state-requests", { params: { rng: r } }).then(({ data }) => setD(data)).catch(() => setD(null));
  }, [rng]);
  if (!d) return null;
  const max = Math.max(1, ...(d.top_states || []).map((x) => x.count));
  return (
    <div className="card-thali p-4" data-testid="an-state-requests">
      <div className="font-heading font-bold text-sm mb-3 flex items-center gap-2">🗺️ State Requests <span className="text-xs font-normal text-thali-ink/50">(other states wanting the calculator)</span></div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <Card label="Section views" value={d.viewed} icon={Eye} />
        <Card label="States picked" value={d.selected} />
        <Card label="WhatsApp taps" value={d.taps} />
      </div>
      {(d.top_states || []).length === 0 ? (
        <p className="text-sm text-thali-ink/50">No state requests yet.</p>
      ) : (
        <div className="space-y-2" data-testid="an-top-states">
          {d.top_states.map((s) => (
            <div key={s.state}>
              <div className="flex justify-between text-xs mb-1"><span className="font-semibold">{s.state}</span><span className="font-num font-bold">{s.count}</span></div>
              <div className="h-5 rounded-lg bg-thali-cream overflow-hidden"><div className="h-full rounded-lg" style={{ width: `${(s.count / max) * 100}%`, background: "#0B6B3A", minWidth: 6 }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VisitorsTab({ d }) {
  const c = d.cards;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="Unique visitors" value={c.unique_visitors.value} delta={c.unique_visitors.delta} icon={Users} />
        <Card label="Page views" value={c.page_views.value} delta={c.page_views.delta} icon={Eye} />
        <Card label="Sessions" value={c.sessions.value} icon={Radio} />
        <Card label="Avg duration" value={fmtDur(c.avg_duration.value)} icon={Timer} />
        <Card label="Bounce rate" value={c.bounce_rate.value} suffix="%" icon={Percent} />
      </div>
      <ChartBox title="Visitors per day">
        <LineChart data={d.visitors_series}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="day" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Legend />
          <Line type="monotone" dataKey="visitors" stroke="#2E7D32" strokeWidth={2} /><Line type="monotone" dataKey="views" stroke="#EA580C" strokeWidth={2} /></LineChart>
      </ChartBox>
      <div className="grid md:grid-cols-2 gap-4">
        <ChartBox title="Device split"><Donut data={d.devices} /></ChartBox>
        <ChartBox title="Traffic sources">
          <BarChart data={d.sources}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="name" fontSize={10} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Bar dataKey="value" radius={[6, 6, 0, 0]}>{d.sources.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar></BarChart>
        </ChartBox>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-thali p-4">
          <div className="font-heading font-bold text-sm mb-2">Top pages</div>
          <table className="w-full text-sm"><tbody>{d.top_pages.map((p, i) => (<tr key={i} className="border-t border-thali-turmeric/10"><td className="py-1.5">{p.path}</td><td className="py-1.5 text-right font-num font-bold">{p.views}</td></tr>))}</tbody></table>
        </div>
        <div className="card-thali p-4">
          <div className="font-heading font-bold text-sm mb-3">Registration funnel</div>
          <Funnel steps={d.funnel} />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-thali p-4"><div className="font-heading font-bold text-sm mb-2">Top districts (Odisha)</div>
          {d.districts.length ? <table className="w-full text-sm"><tbody>{d.districts.map((s, i) => (<tr key={i} className="border-t border-thali-turmeric/10"><td className="py-1.5">{s.name}</td><td className="py-1.5 text-right font-num font-bold">{s.value}</td></tr>))}</tbody></table> : <div className="text-sm text-thali-ink/50">No location data yet.</div>}</div>
        <div className="card-thali p-4"><div className="font-heading font-bold text-sm mb-2">Referral-link visits</div>
          {d.ref_codes.length ? <table className="w-full text-sm"><tbody>{d.ref_codes.map((s, i) => (<tr key={i} className="border-t border-thali-turmeric/10"><td className="py-1.5 font-mono">{s.code}</td><td className="py-1.5 text-right font-num font-bold">{s.visits}</td></tr>))}</tbody></table> : <div className="text-sm text-thali-ink/50">No referral-link visits yet.</div>}</div>
      </div>
    </div>
  );
}

function CohortTable({ cohorts }) {
  if (!cohorts || !cohorts.length) return <div className="text-sm text-thali-ink/50">Not enough data yet.</div>;
  const maxLen = Math.max(...cohorts.map((c) => c.retention.length));
  const shade = (v) => v >= 60 ? "#0B6B3A" : v >= 40 ? "#2E7D32" : v >= 20 ? "#7CB342" : v > 0 ? "#C5E1A5" : "#F1F5F0";
  return (
    <div className="overflow-x-auto" data-testid="an-cohorts">
      <table className="text-xs min-w-[520px]">
        <thead><tr className="text-thali-ink/60"><th className="text-left p-1.5">Cohort (week)</th><th className="p-1.5">Users</th>{Array.from({ length: maxLen }).map((_, i) => <th key={i} className="p-1.5">W{i}</th>)}</tr></thead>
        <tbody>
          {cohorts.map((c, i) => (
            <tr key={i}><td className="p-1.5 font-medium whitespace-nowrap">{c.cohort}</td><td className="p-1.5 text-center font-num">{c.size}</td>
              {c.retention.map((v, j) => (<td key={j} className="p-1"><div className="rounded text-center py-1 font-semibold" style={{ background: shade(v), color: v >= 40 ? "#fff" : "#1B2A21" }}>{v}%</div></td>))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const HM_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
function Heatmap({ cells }) {
  if (!cells || !cells.length) return <div className="text-sm text-thali-ink/50">No usage data yet.</div>;
  const max = Math.max(1, ...cells.map((c) => c.value));
  const at = (dw, h) => (cells.find((c) => c.day === dw && c.hour === h) || {}).value || 0;
  const bg = (v) => v === 0 ? "#F1F5F0" : `rgba(11,107,58,${0.15 + 0.85 * (v / max)})`;
  return (
    <div className="overflow-x-auto" data-testid="an-heatmap">
      <table className="border-separate" style={{ borderSpacing: 2 }}>
        <thead><tr><th></th>{Array.from({ length: 24 }).map((_, h) => <th key={h} className="text-[8px] text-thali-ink/40 font-normal">{h % 3 === 0 ? h : ""}</th>)}</tr></thead>
        <tbody>
          {HM_DAYS.map((day, dw) => (
            <tr key={dw}><td className="text-[10px] text-thali-ink/60 pr-1">{day}</td>
              {Array.from({ length: 24 }).map((_, h) => { const v = at(dw, h); return <td key={h}><div title={`${day} ${h}:00 — ${v}`} className="w-3.5 h-3.5 rounded-sm" style={{ background: bg(v) }} /></td>; })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-thali-ink/40 mt-1">Times in IST · darker = more activity</div>
    </div>
  );
}

function UsersTab({ d }) {
  const c = d.cards;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card label="Total users" value={c.total_users.value} icon={Users} />
        <Card label="New users" value={c.new_users.value} delta={c.new_users.delta} icon={TrendingUp} />
        <Card label="DAU" value={c.dau.value} />
        <Card label="WAU" value={c.wau.value} />
        <Card label="MAU" value={c.mau.value} />
        <Card label="Used all free" value={c.used_all_free.value} />
      </div>
      <ChartBox title="New registrations per day">
        <BarChart data={d.registrations_series}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="day" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#2E7D32" radius={[6, 6, 0, 0]} /></BarChart>
      </ChartBox>
      <div className="grid md:grid-cols-2 gap-4">
        <ChartBox title="Calculations per day (free vs paid)">
          <LineChart data={d.calc_series}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="day" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Legend />
            <Line type="monotone" dataKey="free" stroke="#EA580C" strokeWidth={2} /><Line type="monotone" dataKey="paid" stroke="#2E7D32" strokeWidth={2} /></LineChart>
        </ChartBox>
        <ChartBox title="Day-type split"><Donut data={d.daytype} /></ChartBox>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <ChartBox title="Active users per day">
          <LineChart data={d.active_series}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="day" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="value" stroke="#2563EB" strokeWidth={2} /></LineChart>
        </ChartBox>
        <ChartBox title="Downloads">
          <BarChart data={d.downloads}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Bar dataKey="value" radius={[6, 6, 0, 0]}>{d.downloads.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}</Bar></BarChart>
        </ChartBox>
      </div>
      <div className="card-thali p-4"><div className="font-heading font-bold text-sm mb-3">Weekly retention cohorts</div><CohortTable cohorts={d.cohorts} /></div>
      <div className="card-thali p-4"><div className="font-heading font-bold text-sm mb-3">Usage heatmap (day × hour)</div><Heatmap cells={d.heatmap} /></div>
    </div>
  );
}

function SubscribersTab({ d, revealed, reveal, maskMobile, waCopy, exportXlsx }) {
  const c = d.cards;
  const [win, setWin] = useState("d7");
  const list = d.expiry[win] || [];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Active subscribers" value={c.active_subscribers.value} icon={Users} />
        <Card label="New subscribers" value={c.new_subscribers.value} delta={c.new_subscribers.delta} icon={TrendingUp} />
        <Card label="Revenue" value={`₹${c.revenue.value}`} delta={c.revenue.delta} icon={IndianRupee} />
        <Card label="Total revenue" value={`₹${c.total_revenue.value}`} icon={IndianRupee} />
        <Card label="Avg / day" value={`₹${c.avg_per_day.value}`} />
        <Card label="Paywall conv." value={c.conversion.value} suffix="%" icon={Percent} />
        <Card label="Renewal rate" value={c.renewal_rate.value} suffix="%" />
        <Card label="Expired" value={c.expired.value} />
      </div>
      <ChartBox title="New subscriptions & revenue per day">
        <LineChart data={d.subs_series}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="day" fontSize={11} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Legend />
          <Line type="monotone" dataKey="subs" stroke="#2E7D32" strokeWidth={2} /><Line type="monotone" dataKey="revenue" stroke="#EA580C" strokeWidth={2} /></LineChart>
      </ChartBox>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-thali p-4"><div className="font-heading font-bold text-sm mb-3">Paywall funnel <span className="text-xs text-thali-ink/50">· {d.failed_payments} failed</span></div><Funnel steps={d.paywall_funnel} /></div>
        <div className="card-thali p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading font-bold text-sm">Expiring soon</div>
            <button onClick={() => exportXlsx("expiry")} className="btn-ghost text-xs px-3 min-h-[36px]" data-testid="an-export-expiry"><Download className="w-4 h-4" /> Excel</button>
          </div>
          <div className="flex gap-2 mb-2">{[["d7", "7d"], ["d15", "15d"], ["d30", "30d"]].map(([k, l]) => (<button key={k} onClick={() => setWin(k)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${win === k ? "bg-thali-green text-white" : "bg-thali-cream"}`}>{l}</button>))}</div>
          {list.length ? list.map((r) => (
            <div key={r.id} className="flex items-center justify-between border-t border-thali-turmeric/10 py-2 text-sm">
              <div><div className="font-medium">{r.name}</div><div className="text-xs text-thali-ink/50">{revealed[r.id] || maskMobile(r.mobile)} {!revealed[r.id] && <button onClick={() => reveal(r.id)} className="text-thali-green underline ml-1" data-testid={`an-reveal-${r.id}`}>show</button>} · exp {(r.expiry || "").slice(0, 10)}</div></div>
              <button onClick={() => waCopy(r)} className="btn-ghost text-xs px-2 min-h-[36px]" data-testid={`an-wa-${r.id}`}><Copy className="w-4 h-4" /></button>
            </div>
          )) : <div className="text-sm text-thali-ink/50">None expiring in this window.</div>}
        </div>
      </div>

      {/* Recharge reward economics */}
      {d.recharge && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="an-recharge-cards">
          <Card label="Recharge pending" value={d.recharge.pending} icon={Smartphone} />
          <Card label="Recharge done" value={d.recharge.done} />
          <Card label="Recharge rejected" value={d.recharge.rejected} />
          <Card label="Recharge cost (month)" value={`₹${d.recharge.month_cost}`} icon={IndianRupee} />
        </div>
      )}
      {d.revenue_methods && (
        <div className="card-thali p-4" data-testid="an-revenue-methods">
          <div className="font-heading font-bold text-sm mb-2">Revenue by payment method</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-thali-cream p-3">
              <div className="text-xs text-thali-ink/60">Online checkout (Razorpay)</div>
              <div className="font-num font-extrabold text-xl text-thali-green">₹{d.revenue_methods.online.amount}</div>
              <div className="text-xs text-thali-ink/50">{d.revenue_methods.online.count} payments</div>
            </div>
            <div className="rounded-xl bg-thali-turmeric/20 p-3">
              <div className="text-xs text-thali-ink/60">UPI QR (approved)</div>
              <div className="font-num font-extrabold text-xl text-thali-saffron">₹{d.revenue_methods.upi_qr.amount}</div>
              <div className="text-xs text-thali-ink/50">{d.revenue_methods.upi_qr.count} payments</div>
            </div>
          </div>
          {d.revenue_methods.total > 0 && (
            <div className="mt-2 h-2 rounded-full overflow-hidden bg-thali-cream flex">
              <div className="h-full bg-thali-green" style={{ width: `${Math.round(d.revenue_methods.online.amount / d.revenue_methods.total * 100)}%` }} />
              <div className="h-full bg-thali-saffron" style={{ width: `${Math.round(d.revenue_methods.upi_qr.amount / d.revenue_methods.total * 100)}%` }} />
            </div>
          )}
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {d.referral_revenue && (
          <div className="card-thali p-4" data-testid="an-ref-revenue">
            <div className="font-heading font-bold text-sm mb-1">Referral revenue attribution</div>
            <div className="text-xs text-thali-ink/60 mb-2">Revenue from referred paying users: <b>₹{d.referral_revenue.total}</b>{d.recharge ? ` · reward cost (recharge done): ₹${d.recharge.total_cost}` : ""}</div>
            {(d.referral_revenue.top || []).length ? (
              <table className="w-full text-sm"><tbody>{d.referral_revenue.top.map((r, i) => (<tr key={i} className="border-t border-thali-turmeric/10"><td className="py-1.5">{r.referrer}</td><td className="py-1.5 text-right font-num font-bold">₹{r.revenue}</td></tr>))}</tbody></table>
            ) : <div className="text-sm text-thali-ink/50">No referred revenue yet.</div>}
          </div>
        )}
        {d.retention && (
          <div className="card-thali p-4" data-testid="an-churn-lowusage">
            <div className="font-heading font-bold text-sm mb-2">Churn & low-usage (win-back)</div>
            <div className="text-xs text-thali-ink/60 mb-1">Churned (expired ≤30d): <b>{d.retention.churned.length}</b> · Low-usage active (&lt;3 entries/30d): <b>{d.retention.low_usage.length}</b></div>
            <div className="max-h-40 overflow-y-auto">
              {[...d.retention.churned.map((r) => ({ ...r, tag: "churned" })), ...d.retention.low_usage.map((r) => ({ ...r, tag: "low" }))].slice(0, 30).map((r, i) => (
                <div key={i} className="flex items-center justify-between border-t border-thali-turmeric/10 py-1.5 text-sm">
                  <div><span className="font-medium">{r.name}</span> <span className="text-xs text-thali-ink/50">{maskMobile(r.mobile)}</span></div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${r.tag === "churned" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{r.tag === "churned" ? "Churned" : `${r.entries} entries`}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
